import { useState } from "react";
import { api } from "@/lib/api";
import { Scissors, RefreshCw } from "lucide-react";
import { toast } from "sonner";

const KEY_STORE = "kc_admin_key";

// /admin/alterations — every measurement request submitted with a purchase.
export default function AdminAlterations() {
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem(KEY_STORE) || "");
  const [rows, setRows] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = async () => {
    if (!adminKey) { toast.error("Enter your admin key."); return; }
    localStorage.setItem(KEY_STORE, adminKey);
    setLoading(true);
    try {
      const { data } = await api.get("/alterations", { headers: { "X-Admin-Key": adminKey } });
      setRows(data.alterations || []);
    } catch {
      toast.error("Could not load — check your admin key.");
      localStorage.removeItem(KEY_STORE);
    }
    setLoading(false);
  };

  const fmtMeasure = (m) =>
    Object.entries(m || {})
      .filter(([k, v]) => v && k !== "notes")
      .map(([k, v]) => `${k.replaceAll("_", " ")}: ${v}`)
      .join(" · ");

  return (
    <div className="max-w-[1100px] mx-auto px-5 py-10 font-body" data-testid="admin-alterations">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <h1 className="font-display text-3xl flex items-center gap-2">
          <Scissors size={22} className="text-[#B84A3B]" /> Alteration Requests
        </h1>
        <div className="flex items-center gap-2">
          <input
            type="password"
            value={adminKey}
            onChange={(e) => setAdminKey(e.target.value)}
            placeholder="Admin key"
            data-testid="admin-alterations-key"
            className="border border-[#E5E5DF] px-3 py-2 text-sm w-56 focus:outline-none focus:border-[#B84A3B]"
          />
          <button onClick={load} data-testid="admin-alterations-load"
            className="bg-[#1A1A1A] text-white px-4 py-2 text-[0.7rem] font-mono tracking-[0.15em] uppercase hover:bg-[#B84A3B] transition-colors flex items-center gap-2">
            <RefreshCw size={13} /> Load
          </button>
        </div>
      </div>

      {rows && rows.length === 0 && (
        <p className="mt-10 text-[#6E6E69]" data-testid="admin-alterations-empty">No alteration requests yet.</p>
      )}

      {rows && rows.length > 0 && (
        <div className="mt-6 space-y-3">
          {rows.map((r, i) => (
            <div key={i} className="border border-[#E5E5DF] p-4" data-testid={`alteration-row-${i}`}>
              <div className="flex justify-between gap-3 flex-wrap">
                <div>
                  <p className="font-medium text-sm">
                    {(r.options || []).map((o) => o.label).filter(Boolean).join(" + ") || r.option_label}
                    {(r.total_price || 0) > 0 && <span className="text-[#B84A3B] font-mono ml-2">${r.total_price}</span>}
                  </p>
                  <p className="text-sm text-[#6E6E69] mt-0.5">
                    for <b className="text-[#1A1A1A]">{r.product_title}</b>
                    {r.size ? ` · Size ${r.size}` : ""}{r.color ? ` · ${r.color}` : ""}
                  </p>
                </div>
                <span className="text-xs font-mono text-[#6E6E69]">{new Date(r.created_at).toLocaleString()}</span>
              </div>
              {fmtMeasure(r.measurements) && (
                <p className="mt-2 text-sm font-mono bg-[#EFEFEA] px-3 py-2">{fmtMeasure(r.measurements)}</p>
              )}
              {r.measurements?.notes && (
                <p className="mt-1.5 text-sm text-[#6E6E69] italic">"{r.measurements.notes}"</p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
